import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Chip, Grid } from '@mui/material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { fileSaver } from 'core/utils/common';
import { logger } from 'core/utils/logger';
import { downloadSecurityDepositReport, getSecurityDepositReportData } from 'services/reports';
import DateRangeFilter from './DateRangeFilter';
import ReportsDataTable from './ReportsDataTable';

const RowWrapper = ({ children }) => <Box sx={{ my: 1, width: '100%' }}>{children}</Box>;

const SecurityDepositReportPage = ({ pageRef }) => {
  logger.info('[SecurityDepositReportPage] mounting...');

  const [dateRange, setDateRange] = useState(null);
  const [securityDepositData, setSecurityDepositData] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  const loadData = useCallback((filters) => setDateRange(filters), []);

  const fetchReportData = useCallback((range) => {
    const controller = new AbortController();
    const params = { from: format(range.from, 'dd-MM-yyyy'), to: format(range.to, 'dd-MM-yyyy') };

    getSecurityDepositReportData(params, { signal: controller.signal, pageRef }).then(({ response, error }) => {
      if (error?.message === 'canceled') return;
      if (error) {
        setSecurityDepositData([]);
        return enqueueSnackbar(error?.message, { variant: 'error' });
      }
      setSecurityDepositData(Array.isArray(response) ? response : []);
    });

    return controller;
  }, [enqueueSnackbar, pageRef]);

  useEffect(() => {
    if (!dateRange) return undefined;
    const controller = fetchReportData(dateRange);
    return () => controller.abort();
  }, [dateRange, fetchReportData]);

  const columns = useMemo(() => [
    { field: 'name', headerName: 'Name', minWidth: 150 },
    { field: 'mobile', headerName: 'Mobile Number', minWidth: 150 },
    { field: 'block', headerName: 'Block', minWidth: 100 },
    { field: 'bedInfo', headerName: 'Bed', minWidth: 140 },
    { field: 'paymentDate', headerName: 'Payment Date', minWidth: 160 },
    { field: 'paymentMode', headerName: 'Payment Mode', minWidth: 150 },
    { field: 'amountToBePaid', headerName: 'Amount To Be Paid', minWidth: 180 },
    { field: 'amountPaid', headerName: 'Amount Paid', minWidth: 140 },
    { field: 'amountDue', headerName: 'Amount Due', minWidth: 140 },
    { field: 'discount', headerName: 'Discount', minWidth: 120 },
    { field: 'tax', headerName: 'Tax', minWidth: 100 },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 130,
      renderCell: ({ value }) => (
        <RowWrapper>
          <Chip color={value === 'PAID' ? 'success' : 'error'} label={(value || 'Unknown').toLowerCase()} size="small" sx={{ textTransform: 'capitalize' }} />
        </RowWrapper>
      ),
    },
    // {
    //   field: 'comments',
    //   headerName: 'Comments',
    //   minWidth: 240,
    //   renderCell: ({ value }) => <RowWrapper><Box component="div" whiteSpace="normal">{value}</Box></RowWrapper>,
    // },
    { field: 'createdBy', headerName: 'Created By', minWidth: 140 },
  ], []);

  const downloadFile = useCallback(() => {
    if (!dateRange) return;
    const params = { from: format(dateRange.from, 'dd-MM-yyyy'), to: format(dateRange.to, 'dd-MM-yyyy') };

    downloadSecurityDepositReport(params, { pageRef }).then(({ response, error }) => {
      if (error?.message === 'canceled') return;
      if (error) return enqueueSnackbar(error?.message, { variant: 'error' });

      fileSaver(response, `Security_Deposit_Report_${new Date().toISOString().replace(/:/, '.')}.csv`);
    });
  }, [dateRange, enqueueSnackbar, pageRef]);

  return (
    <Grid container p={1}>
      <DateRangeFilter loadData={loadData} downloadFile={downloadFile} />
      <ReportsDataTable rows={securityDepositData} columns={columns} />
    </Grid>
  );
};

export default SecurityDepositReportPage;
